/**
 * Supabase side of the reports (ADR 0018): the SECURITY INVOKER report functions through their JSON
 * wrappers (one value per call, so PostgREST's row cap does not cut a report), the KPI RPC and the export
 * log. Only the user's JWT is used; RLS decides what comes back.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  REPORT_LIMITS,
  type AlarmType,
  type ChecklistComplianceRow,
  type DrivingMetrics,
  type DrivingResult,
  type DrivingRow,
  type EffectiveBasis,
  type EventRow,
  type ExportFormat,
  type HeatmapResult,
  type KpiPanel,
  type KpiPeriod,
  type ReportResult,
  type RoutePoint,
  type SpaghettiPointTuple,
  type SpaghettiResult,
  type UsageRow,
} from "@fts/core";
import type { DrivingQuery, EventReportQuery, HeatmapQuery, ReportQuery, RouteQuery, SpaghettiQuery } from "./reports";

type PgError = { message: string; code?: string };
type Res<T> = { data: T | null; error: PgError | null };
type Check = <T>(res: Res<T>) => T;

type UsageJson = {
  vehicle_id: string; fleet_no: string; site_id: string; day: string; tz: string;
  ignition_s: number; moving_s: number; idle_s: number; distance_m: number;
  lift_count: number | null; lifted_kg: number | null; lift_estimated: boolean | null;
  operator_ids: string[] | null; operator_names: string[] | null; operators_hidden: number; operators_unknown?: number;
  basis: EffectiveBasis; basis_s: number; basis_ign_s: number; utilisation: number | null;
};
type EventJson = {
  id: number | string; occurred_at: string; type: AlarmType; severity: number; status: "open" | "acknowledged";
  vehicle_id: string; fleet_no: string | null; site_id: string | null; site_name: string | null;
  operator_id: string | null; operator_name: string | null; off_hours: boolean; delayed: boolean; time_unknown: boolean;
  repeat_count: number; acked_at: string | null; ack_note: string | null; geofence_id: string | null; geofence_name: string | null;
  shift_name: string | null; tz: string;
};
type RouteJson = [string, number, number, number | null, boolean, string | null, number | null, string | null, string | null];
type ChecklistJson = {
  vehicle_id: string; fleet_no: string; site_id: string; shift_name: string | null; has_shifts: boolean;
  shifts_used: number | null; shifts_checked: number | null; missed: number | null; submissions: number; compliance: number | null;
  faults_opened: number; faults_resolved: number; mttr_h: number | null; tz: string;
};

const iso = (v: string) => new Date(v).toISOString();
const vehicles = (q: ReportQuery) => (q.vehicleIds.length ? q.vehicleIds : null);

/** The report time zone the server used; the default when there were no rows. */
async function scopeTz(db: SupabaseClient, q: { tenantId: string; siteId: string | null }, rows: { tz?: string }[]): Promise<string> {
  if (rows[0]?.tz) return rows[0].tz;
  const res = await db.rpc("fts_report_tz", { p_tenant: q.tenantId, p_site: q.siteId });
  return typeof res.data === "string" ? res.data : "Europe/Istanbul";
}

export function toUsageRow(r: UsageJson): UsageRow {
  return {
    vehicleId: r.vehicle_id, fleetNo: r.fleet_no, siteId: r.site_id, day: String(r.day).slice(0, 10),
    ignitionS: r.ignition_s, movingS: r.moving_s, idleS: r.idle_s, distanceM: r.distance_m,
    liftCount: r.lift_count, liftedKg: r.lifted_kg, liftEstimated: r.lift_estimated,
    operatorIds: r.operator_ids ?? [], operatorNames: r.operator_names ?? [], operatorsHidden: r.operators_hidden ?? 0,
    operatorsUnknown: r.operators_unknown ?? 0,
    basis: r.basis, basisS: r.basis_s, basisIgnitionS: r.basis_ign_s, utilisation: r.utilisation,
  };
}

export function toEventRow(r: EventJson): EventRow {
  return {
    id: String(r.id), occurredAt: iso(r.occurred_at), type: r.type, severity: r.severity, status: r.status,
    vehicleId: r.vehicle_id, fleetNo: r.fleet_no, siteId: r.site_id, siteName: r.site_name,
    operatorId: r.operator_id, operatorName: r.operator_name, offHours: r.off_hours, delayed: r.delayed, timeUnknown: r.time_unknown,
    repeatCount: r.repeat_count, ackedAt: r.acked_at ? iso(r.acked_at) : null, ackNote: r.ack_note,
    geofenceId: r.geofence_id, geofenceName: r.geofence_name, shiftName: r.shift_name,
  };
}

export function toRoutePoint(r: RouteJson): RoutePoint {
  return { ts: iso(r[0]), lat: r[1], lon: r[2], speedKmh: r[3], ignition: r[4], src: r[5], acc: r[6], operatorId: r[7], operatorName: r[8] };
}

export function toChecklistRow(r: ChecklistJson): ChecklistComplianceRow {
  return {
    vehicleId: r.vehicle_id, fleetNo: r.fleet_no, siteId: r.site_id, shiftName: r.shift_name, hasShifts: r.has_shifts,
    shiftsUsed: r.shifts_used, shiftsChecked: r.shifts_checked, missed: r.missed, submissions: r.submissions, compliance: r.compliance,
    faultsOpened: r.faults_opened, faultsResolved: r.faults_resolved, mttrH: r.mttr_h,
  };
}

export async function reportUsage(db: SupabaseClient, check: Check, q: ReportQuery): Promise<ReportResult<UsageRow>> {
  const rows = check<UsageJson[]>(await db.rpc("report_usage_json", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: vehicles(q), p_operator: q.operatorId, p_from: q.from, p_to: q.to, p_basis: q.basis ?? "auto",
  }));
  return { rows: rows.map(toUsageRow), tz: await scopeTz(db, q, rows), truncated: false };
}

/* ---- exports: export_report() returns the data and writes the audit row in the same call ---- */

/** The report arguments as export_report() takes them (and logs them: ids, dates, types, basis; no names). */
export function exportArgs(q: ReportQuery & { types?: string[] }): Record<string, unknown> {
  return {
    tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, operatorId: q.operatorId, from: q.from, to: q.to,
    ...(q.basis ? { basis: q.basis } : {}), ...(q.types ? { types: q.types } : {}),
  };
}

async function exportRows<T>(db: SupabaseClient, check: Check, report: string, format: ExportFormat, args: Record<string, unknown>): Promise<T[]> {
  return check<T[]>(await db.rpc("export_report", { p_report: report, p_format: format, p_args: args }));
}

export async function exportUsage(db: SupabaseClient, check: Check, q: ReportQuery, format: ExportFormat): Promise<ReportResult<UsageRow>> {
  const rows = await exportRows<UsageJson>(db, check, "usage", format, exportArgs(q));
  return { rows: rows.map(toUsageRow), tz: await scopeTz(db, q, rows), truncated: false };
}

export async function exportEvents(db: SupabaseClient, check: Check, q: EventReportQuery, format: ExportFormat): Promise<ReportResult<EventRow>> {
  const rows = await exportRows<EventJson>(db, check, "events", format, exportArgs(q));
  return { rows: rows.map(toEventRow), tz: await scopeTz(db, q, rows), truncated: false };
}

export async function exportRoute(db: SupabaseClient, check: Check, q: RouteQuery, format: ExportFormat, tz: string): Promise<ReportResult<RoutePoint>> {
  const rows = await exportRows<RouteJson>(db, check, "route", format, { vehicleId: q.vehicleId, from: q.from, to: q.to });
  return { rows: rows.map(toRoutePoint), tz, truncated: false };
}

export async function exportChecklist(db: SupabaseClient, check: Check, q: ReportQuery, format: ExportFormat): Promise<ReportResult<ChecklistComplianceRow>> {
  const rows = await exportRows<ChecklistJson>(db, check, "checklist", format, exportArgs(q));
  return { rows: rows.map(toChecklistRow), tz: await scopeTz(db, q, rows), truncated: false };
}

export async function reportEvents(db: SupabaseClient, check: Check, q: EventReportQuery): Promise<ReportResult<EventRow>> {
  const limit = REPORT_LIMITS.exportMaxRows;
  const rows = check<EventJson[]>(await db.rpc("report_events_json", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: vehicles(q), p_operator: q.operatorId, p_from: q.from, p_to: q.to,
    p_types: q.types.length ? q.types : null, p_limit: limit,
  }));
  return { rows: rows.slice(0, limit).map(toEventRow), tz: await scopeTz(db, q, rows), truncated: rows.length > limit };
}

export async function reportRoute(db: SupabaseClient, check: Check, q: RouteQuery, tz: string): Promise<ReportResult<RoutePoint>> {
  const limit = REPORT_LIMITS.routeMaxPoints;
  const rows = check<RouteJson[]>(await db.rpc("report_route_json", { p_vehicle: q.vehicleId, p_from: q.from, p_to: q.to, p_limit: limit }));
  return { rows: rows.slice(0, limit).map(toRoutePoint), tz, truncated: rows.length > limit };
}

export async function reportChecklist(db: SupabaseClient, check: Check, q: ReportQuery): Promise<ReportResult<ChecklistComplianceRow>> {
  const rows = check<ChecklistJson[]>(await db.rpc("report_checklist_json", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: vehicles(q), p_from: q.from, p_to: q.to,
  }));
  return { rows: rows.map(toChecklistRow), tz: await scopeTz(db, q, rows), truncated: false };
}

/* ---- heatmap (ADR 0024): one json value with cells [lat, lon, weight, count] and totals ---- */

type HeatmapJson = {
  tz: string; mode: HeatmapResult["mode"]; cell_m: number; cells: [number | string, number | string, number | string, number | string][];
  total_cells: number; truncated: boolean; points: number | string; weight: number | string; max_weight: number | string;
  vehicles: number; unlocated: number; bounds: [number | string, number | string, number | string, number | string] | null;
};

export function toHeatmap(r: HeatmapJson): HeatmapResult {
  const n = (v: number | string) => Number(v);
  return {
    tz: r.tz, mode: r.mode, cellM: n(r.cell_m),
    cells: (r.cells ?? []).map((c) => ({ lat: n(c[0]), lon: n(c[1]), weight: n(c[2]), count: n(c[3]) })),
    totalCells: n(r.total_cells), truncated: !!r.truncated, points: n(r.points), weight: n(r.weight), maxWeight: n(r.max_weight),
    vehicles: n(r.vehicles), unlocated: n(r.unlocated),
    bounds: r.bounds ? [n(r.bounds[0]), n(r.bounds[1]), n(r.bounds[2]), n(r.bounds[3])] : null,
  };
}

/** The heatmap arguments as export_report() takes and logs them (ids, dates, mode, types; no names). */
export function heatmapArgs(q: HeatmapQuery): Record<string, unknown> {
  return { tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, from: q.from, to: q.to, mode: q.mode, types: q.mode === "events" ? q.types : [] };
}

export async function reportHeatmap(db: SupabaseClient, check: Check, q: HeatmapQuery): Promise<HeatmapResult> {
  return toHeatmap(check<HeatmapJson>(await db.rpc("report_heatmap", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null, p_from: q.from, p_to: q.to,
    p_mode: q.mode, p_types: q.mode === "events" && q.types.length ? q.types : null,
  })));
}

export async function exportHeatmap(db: SupabaseClient, check: Check, q: HeatmapQuery, format: ExportFormat): Promise<HeatmapResult> {
  return toHeatmap(check<HeatmapJson>(await db.rpc("export_report", { p_report: "heatmap", p_format: format, p_args: heatmapArgs(q) })));
}

/* ---- spaghetti diagram (ADR 0025): one json value with thinned segments per vehicle and totals ---- */

type Num = number | string;
type SpaghettiJson = {
  tz: string; bucket_s: Num; step: Num; spacing_s: Num; max_points: Num; truncated: boolean;
  vehicles: { id: string; name: string; segments: [Num, Num, Num, Num][][]; points: Num; moving_points: Num; distance_m: Num; moving_s: Num }[];
  totals: { vehicles: Num; points: Num; moving_points: Num; distance_m: Num; moving_s: Num };
  bounds: [Num, Num, Num, Num] | null;
};

export function toSpaghetti(r: SpaghettiJson): SpaghettiResult {
  const n = (v: Num) => Number(v);
  return {
    tz: r.tz, bucketS: n(r.bucket_s), step: n(r.step), spacingS: n(r.spacing_s), maxPoints: n(r.max_points), truncated: !!r.truncated,
    vehicles: (r.vehicles ?? []).map((v) => ({
      id: v.id, name: v.name, segments: (v.segments ?? []).map((s) => s.map((p) => [n(p[0]), n(p[1]), n(p[2]), n(p[3])] as SpaghettiPointTuple)),
      points: n(v.points), movingPoints: n(v.moving_points), distanceM: n(v.distance_m), movingS: n(v.moving_s),
    })),
    totals: { vehicles: n(r.totals.vehicles), points: n(r.totals.points), movingPoints: n(r.totals.moving_points), distanceM: n(r.totals.distance_m), movingS: n(r.totals.moving_s) },
    bounds: r.bounds ? [n(r.bounds[0]), n(r.bounds[1]), n(r.bounds[2]), n(r.bounds[3])] : null,
  };
}

/** The spaghetti arguments as export_report() takes and logs them (ids and dates; no names, no operator). */
export function spaghettiArgs(q: SpaghettiQuery): Record<string, unknown> {
  return { tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, from: q.from, to: q.to };
}

export async function reportSpaghetti(db: SupabaseClient, check: Check, q: SpaghettiQuery): Promise<SpaghettiResult> {
  return toSpaghetti(check<SpaghettiJson>(await db.rpc("report_spaghetti", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null, p_from: q.from, p_to: q.to,
  })));
}

export async function exportSpaghetti(db: SupabaseClient, check: Check, q: SpaghettiQuery, format: ExportFormat): Promise<SpaghettiResult> {
  return toSpaghetti(check<SpaghettiJson>(await db.rpc("export_report", { p_report: "spaghetti", p_format: format, p_args: spaghettiArgs(q) })));
}

/* ---- driving behaviour (ADR 0026): one json value with rows per operator or vehicle and totals ---- */

type DrivingMetricsJson = {
  ignition_s: Num | null; moving_s: Num | null; distance_m: Num | null; avg_kmh: Num | null; max_kmh: Num | null; speed_outliers: Num | null;
  band_s: Num[] | null; impacts: Num[] | null; events: Record<string, Num> | null; events_total: Num | null; events_per_10h: Num | null; impacts_per_10h: Num | null;
};
type DrivingJson = {
  tz: string; group: DrivingResult["group"]; bands_kmh: Num[]; max_plausible_kmh: Num; rate_hours: Num; rate_min_moving_s: Num;
  event_types: string[] | null;
  rows: (DrivingMetricsJson & { kind: DrivingRow["kind"]; id: string | null; name: string | null; cards: Num; vehicles: Num | null; suppressed?: boolean })[];
  totals: DrivingMetricsJson & { operators: Num; vehicles: Num };
};

/** Metrics of a row; a suppressed row (KVKK) has nulls, kept as 0 here and never shown (DrivingRow.suppressed). */
function toDrivingMetrics(r: DrivingMetricsJson): DrivingMetrics {
  const n = (v: Num | null) => Number(v ?? 0);
  const o = (v: Num | null) => (v === null || v === undefined ? null : Number(v));
  const imp = (r.impacts ?? []).map(n);
  return {
    ignitionS: n(r.ignition_s), movingS: n(r.moving_s), distanceM: n(r.distance_m), avgKmh: o(r.avg_kmh), maxKmh: o(r.max_kmh),
    speedOutliers: n(r.speed_outliers), bandS: (r.band_s ?? [0, 0, 0, 0]).map(n), impacts: [imp[0] ?? 0, imp[1] ?? 0, imp[2] ?? 0],
    events: Object.fromEntries(Object.entries(r.events ?? {}).map(([k, v]) => [k, n(v)])), eventsTotal: n(r.events_total),
    eventsPer10h: o(r.events_per_10h), impactsPer10h: o(r.impacts_per_10h),
  };
}

export function toDriving(r: DrivingJson): DrivingResult {
  const n = (v: Num) => Number(v);
  return {
    tz: r.tz, group: r.group, bandsKmh: (r.bands_kmh ?? []).map(n), maxPlausibleKmh: n(r.max_plausible_kmh), rateHours: n(r.rate_hours),
    rateMinMovingS: n(r.rate_min_moving_s), eventTypes: r.event_types ?? [],
    rows: (r.rows ?? []).map((x) => ({ ...toDrivingMetrics(x), kind: x.kind, id: x.id, name: x.name, cards: n(x.cards), vehicles: Number(x.vehicles ?? 0), suppressed: !!x.suppressed })),
    totals: { ...toDrivingMetrics(r.totals), operators: n(r.totals.operators), vehicles: n(r.totals.vehicles) },
  };
}

/** The driving arguments as export_report() takes and logs them (ids, dates, grouping; no names). */
export function drivingArgs(q: DrivingQuery): Record<string, unknown> {
  return { tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, operatorId: q.operatorId, from: q.from, to: q.to, group: q.group };
}

export async function reportDriving(db: SupabaseClient, check: Check, q: DrivingQuery): Promise<DrivingResult> {
  return toDriving(check<DrivingJson>(await db.rpc("report_driving", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null, p_operator: q.operatorId,
    p_from: q.from, p_to: q.to, p_group: q.group,
  })));
}

export async function exportDriving(db: SupabaseClient, check: Check, q: DrivingQuery, format: ExportFormat): Promise<DrivingResult> {
  return toDriving(check<DrivingJson>(await db.rpc("export_report", { p_report: "driving", p_format: format, p_args: drivingArgs(q) })));
}

export async function kpiPanel(db: SupabaseClient, check: Check, tenantId: string, siteId: string | null, period: KpiPeriod): Promise<KpiPanel> {
  return check<KpiPanel>(await db.rpc("kpi_panel", { p_tenant: tenantId, p_site: siteId, p_period: period }));
}


/** Time zone of a vehicle's site (route report: the window is shown in the site's zone). */
export async function vehicleTz(db: SupabaseClient, siteId: string): Promise<string> {
  const res = await db.rpc("fts_site_tz", { p_site: siteId });
  return typeof res.data === "string" ? res.data : "Europe/Istanbul";
}
